/**
 * Invoice read-only view, used in:
 *   - /invoice/:id (route)
 *   - Modal after a successful POS transaction
 *   - Print preview
 *
 * Visual mirror of the PDF layout but using the design system.
 */

import { format } from 'date-fns';
import { Building2, Phone, Mail, Hash } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Chip } from '@/components/ui/Chip';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { formatIDR, formatDate, formatDateTime, cn } from '@/lib/format';
import type { AppTransaction, CompanyInfo, AppEvent } from '@/types/domain';

export interface InvoiceTemplateProps {
  transaction: AppTransaction;
  company?: Pick<CompanyInfo, 'name' | 'address' | 'phone' | 'email' | 'logoUrl' | 'npwp'> | null;
  event?: Pick<AppEvent, 'name' | 'code'> | null;
  className?: string;
  /** Compact mode hides some details — for use in modals. */
  compact?: boolean;
}

export function InvoiceTemplate({
  transaction,
  company,
  event,
  className,
  compact = false,
}: InvoiceTemplateProps) {
  const customer = transaction.customerSnapshot;
  const isPaid = transaction.paymentStatus === 'paid';
  const isPending = transaction.paymentStatus === 'pending_delivery';
  const isPartialReturn = transaction.paymentStatus === 'partially_returned';

  return (
    <Card
      className={cn('print:shadow-none print:border-0', className)}
      padding="none"
    >
      {/* Header */}
      <div className="px-8 pt-8 pb-6 border-b border-line">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-3">
            {company?.logoUrl ? (
              <img
                src={company.logoUrl}
                alt={company.name}
                className="w-12 h-12 rounded object-cover border border-line flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 rounded bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                <Building2 size={20} />
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-section font-display font-semibold text-ink">
                {company?.name ?? 'Ophth POS'}
              </h2>
              {company?.address && (
                <p className="text-small text-ink-subtle mt-0.5">{company.address}</p>
              )}
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-caption text-ink-subtle">
                {company?.phone && (
                  <span className="inline-flex items-center gap-1">
                    <Phone size={10} /> {company.phone}
                  </span>
                )}
                {company?.email && (
                  <span className="inline-flex items-center gap-1">
                    <Mail size={10} /> {company.email}
                  </span>
                )}
              </div>
              {company?.npwp && (
                <p className="text-caption text-ink-subtle mt-0.5">NPWP: {company.npwp}</p>
              )}
            </div>
          </div>

          <div className="text-right flex-shrink-0">
            <p className="text-overline uppercase text-ink-subtle font-medium">Invoice</p>
            <p className="text-subhead font-display font-semibold text-ink font-mono mt-0.5">
              {transaction.invoiceNumber}
            </p>
            <p className="text-caption text-ink-subtle mt-0.5">
              {formatDateTime(transaction.createdAt)}
            </p>
            <div className="mt-2 flex flex-col items-end gap-1">
              <Chip
                variant={
                  isPaid
                    ? 'success'
                    : isPending
                      ? 'warning'
                      : isPartialReturn
                        ? 'info'
                        : 'default'
                }
              >
                {transaction.paymentStatus.replace('_', ' ').toUpperCase()}
              </Chip>
              {transaction.fulfillment === 'later' && (
                <Chip variant="warning">KIRIM NANTI</Chip>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Meta: customer + event */}
      {!compact && (
        <div className="px-8 py-5 border-b border-line grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-overline uppercase text-ink-subtle font-medium">Customer</p>
            {customer ? (
              <div className="mt-1.5">
                <p className="text-body font-medium text-ink">{customer.name}</p>
                {customer.company && (
                  <p className="text-small text-ink-subtle">{customer.company}</p>
                )}
                <p className="text-small text-ink-subtle">{customer.phone}</p>
                {customer.email && (
                  <p className="text-small text-ink-subtle">{customer.email}</p>
                )}
              </div>
            ) : (
              <p className="text-small text-ink-subtle italic mt-1.5">Walk-in customer</p>
            )}
          </div>
          <div>
            <p className="text-overline uppercase text-ink-subtle font-medium">Event</p>
            {event ? (
              <div className="mt-1.5">
                <p className="text-body font-medium text-ink">{event.name}</p>
                <p className="text-caption font-mono text-ink-subtle inline-flex items-center gap-1">
                  <Hash size={10} /> {event.code}
                </p>
              </div>
            ) : (
              <p className="text-small text-ink-subtle italic mt-1.5">—</p>
            )}
            {transaction.cashierName && (
              <p className="text-caption text-ink-subtle mt-2">Kasir: {transaction.cashierName}</p>
            )}
          </div>
        </div>
      )}

      {/* Items */}
      <div className="px-8 py-5">
        <Table>
          <THead>
            <TR>
              <TH>Produk</TH>
              <TH className="text-right">Qty</TH>
              <TH className="text-right">Harga</TH>
              <TH className="text-right">Subtotal</TH>
            </TR>
          </THead>
          <TBody>
            {transaction.items.map((it, idx) => (
              <TR key={it.allocationId ?? `${it.productId}-${idx}`}>
                <TD>
                  <p className="font-medium text-ink">{it.name}</p>
                  {/* Lot and expiry are the buyer's record of what they were
                      handed — the thing a recall or a shelf-life question is
                      answered from — so they print on the line itself. */}
                  {(it.lotNumber || it.expiryDate) && (
                    <p className="text-caption text-ink-subtle">
                      {it.lotNumber && <>Lot {it.lotNumber}</>}
                      {it.lotNumber && it.expiryDate && ' • '}
                      {it.expiryDate && <>ED {formatDate(it.expiryDate)}</>}
                    </p>
                  )}
                  {it.returnedQty > 0 && (
                    <p className="text-caption text-ink-subtle">
                      Returned {it.returnedQty} • Net {it.qty - it.returnedQty}
                    </p>
                  )}
                  {it.promoDiscount > 0 && (
                    <p className="text-caption text-success">Promo − {formatIDR(it.promoDiscount)}</p>
                  )}
                </TD>
                <TD className="text-right tabular-nums">
                  {it.qty}
                  {it.unit ? <span className="text-ink-subtle"> {it.unit}</span> : null}
                </TD>
                <TD className="text-right tabular-nums">{formatIDR(it.price)}</TD>
                <TD className="text-right tabular-nums font-medium">
                  {formatIDR(it.subtotal)}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>

      {/* Totals */}
      <div className="px-8 pb-6 flex justify-end">
        <div className="w-72 space-y-1.5">
          <Row label="Subtotal" value={formatIDR(transaction.subtotal)} />
          {transaction.appliedPromos.map((p) => (
            <Row
              key={p.promoId}
              label={p.name}
              value={`− ${formatIDR(p.amount)}`}
              valueClassName="text-danger"
            />
          ))}
          {transaction.promoDiscount > 0 && transaction.appliedPromos.length === 0 && (
            <Row label="Promo" value={`− ${formatIDR(transaction.promoDiscount)}`} valueClassName="text-danger" />
          )}
          {transaction.discountAmount > 0 && (
            <Row
              label="Diskon"
              value={`− ${formatIDR(transaction.discountAmount)}`}
              valueClassName="text-danger"
            />
          )}
          {transaction.taxAmount > 0 && (
            <Row
              label={`PPN (${transaction.taxRate}%)`}
              value={formatIDR(transaction.taxAmount)}
            />
          )}
          <div className="pt-2 border-t border-line">
            <Row
              label="Total"
              value={formatIDR(transaction.grandTotal)}
              large
            />
          </div>
          <Row
            label="Metode bayar"
            value={transaction.paymentMethod.toUpperCase()}
            muted
          />
          {transaction.paidAmount > transaction.grandTotal && (
            <>
              <Row label="Bayar" value={formatIDR(transaction.paidAmount)} muted />
              <Row label="Kembali" value={formatIDR(transaction.changeAmount)} muted />
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-8 py-4 border-t border-line bg-canvas/30 text-center">
        <p className="text-caption text-ink-subtle">
          Terima kasih atas pembeliannya.
          {transaction.openclawStatus === 'sent' && (
            <> Invoice sudah dikirim via WhatsApp.</>
          )}
          {transaction.openclawStatus === 'failed' && (
            <span className="text-warning"> Pengiriman WhatsApp gagal — coba kirim ulang.</span>
          )}
        </p>
      </div>
    </Card>
  );
}

function Row({
  label,
  value,
  large = false,
  muted = false,
  valueClassName,
}: {
  label: string;
  value: string;
  large?: boolean;
  muted?: boolean;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span
        className={cn(
          muted ? 'text-caption text-ink-subtle' : 'text-small text-ink',
          large && 'text-body font-medium',
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          'tabular-nums',
          muted ? 'text-caption text-ink-subtle' : 'text-small text-ink',
          large && 'text-body font-semibold',
          valueClassName,
        )}
      >
        {value}
      </span>
    </div>
  );
}

// Re-export for use elsewhere
export { format };
